import "@assets/css/bootstrap-grid.css";
import { Button } from "@components/ui/button";
import { Spinner } from "@components/ui/spinner";
import { Switch } from "@components/ui/switch";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDebounce } from "@pages/dashboard/test/hook";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { FormSkeleton } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import { PayementFormData, PaymentSchema } from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import {
  InvoiceData,
  PaymentData,
  PaymentSingleData,
  StudentData,
  UniquePayIdData,
} from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
// import { parseISO } from "date-fns";
// import { Watch } from "lucide-react";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router";

const PaymentForm: React.FC = () => {
  const { searchParams, handleAction } = useHandleAction({});
  const { type } = useParams();
  const pathname = usePathname();
  const isJrp = pathname == "jrp-payment";
  const queryClient = useQueryClient();
  const modalItem = searchParams.get("modal-item");
  // const pageMode = searchParams.get("modal");

  const {
    handleSubmit,
    control,
    // reset,
    register,
    watch,
    setValue,
    reset,
    getValues,

    formState: { errors, dirtyFields },
  } = useForm<PayementFormData>({
    mode: "onChange",
    resolver: zodResolver(PaymentSchema),
  });

  /* set default value */
  const {
    data: fetchData,
    isPending: fetchPending,
    isFetching,
  } = useListData<IndividualQuery<UniquePayIdData>>({
    endpoint: endpoint["uniquePaymentNumber"],
    key: `${pathname}+${modalItem}`,
    window: false,
  });

  const { data: defaultValue } = useListData<
    IndividualQuery<PaymentSingleData>
  >({
    endpoint: endpoint["payment"],
    key: `${pathname}-listing`,
    window: false,
    id: modalItem || "",
    enabled: modalItem ? true : false,
  });
  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);
    if (fetchData?.status === 201 && !fetchPending) {
      const firstResult = fetchData.data;
      setValue("payment_number", firstResult.payment_number);
    }
  }, [fetchData?.data, fetchPending]);

  const { data: invoiceData, isLoading: invoiceDataPending } = useListData<any>({
    endpoint: endpoint["invoice-by-student"],
    params: {
      course_batch__name: watch("course_batch")?.[0]?.["label"],
      student_id: watch("student")?.[0]?.["value"],
    },
    key: `${pathname}-student-select`,
    window: false,
    enabled:
      watch("student")?.length > 0 &&
      watch("course_batch")?.length > 0 &&
      watch("invoice")?.length > 0
        ? true
        : false,
  });

  console.log("this is the invoice data", invoiceData);

  const { data: studentData } = useListData<IndividualQuery<StudentData>>({
    endpoint: endpoint["student"] + watch("student")?.[0]?.["value"],

    key: `${pathname}-student-select`,
    window: false,
    enabled: watch("student")?.length > 0 ? true : false,
  });
  const { mutate: discountMutate, data: discountData } = usePostData();

  // console.log("This is the discounted data", discountData);
  // console.log("This is the dustom discount", watch("custom_discount"));

  const debouncedValue = useDebounce({
    initalValue: watch("discount_code") || "",
    delay: 1000,
  });
  useEffect(() => {
    if (searchParams.get("modal") === "add") {
      if (watch("student")?.length === 0) {
        reset({});
      }
    }
  }, [watch("student"), searchParams, setValue]);

  useEffect(() => {
    if (
      (!!invoiceData?.data?.results?.[0] && !!watch("discount")?.length) ||
      !!debouncedValue?.length
    ) {
      const invoiceDataAny = invoiceData as any;
      let data: any = {
        due_amount: invoiceDataAny?.data?.results?.[0]?.due_amount,
      };
      if (watch("discount")) {
        data.discount_id = watch("discount")?.[0]?.["value"];
      }
      if (debouncedValue) {
        data.discount_code = debouncedValue;
      }
      discountMutate({
        endpoint: endpoint["discount-calculate"],
        data,
      });
    }
  }, [invoiceData, watch("discount"), debouncedValue]);

  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);

    if (searchParams.get("modal") == "add") {
      pathname == "masterclass-payment" && setValue("is_masterclass", true);
      isJrp && setValue("is_jrp", true);
      return reset({ date: new Date().toISOString().split("T")[0] });
    }

    if (fetchData && searchParams.get("modal") !== "add") {
      return reset({
        student: [
          {
            label: defaultValue?.data?.student?.user?.full_name,
            value: defaultValue?.data?.student?.id,
          },
        ],
        discount: !defaultValue?.data?.discount
          ? []
          : [
              {
                label: defaultValue?.data?.discount?.discount_coupon,
                value: defaultValue?.data?.discount?.id,
              },
            ],
        course_batch: [
          {
            label: defaultValue?.data?.invoice?.course_batch.name,
            value: defaultValue?.data?.invoice?.course_batch?.id,
          },
        ],
        discount_code: !defaultValue?.data?.discount
          ? ""
          : defaultValue?.data.discount_code,
        date: defaultValue?.data.date,
        invoice: [
          {
            label: defaultValue?.data?.invoice?.invoice_no,
            value: defaultValue?.data?.invoice?.id,
          },
        ],
        discount_type: !defaultValue?.data?.discount
          ? []
          : [{ label: "discount_type", value: "discount_type" }],
        total_paid_amount: defaultValue?.data.total_paid_amount,
        payment_mode: [
          {
            label: defaultValue?.data.payment_mode.name,
            value: defaultValue?.data.payment_mode.id,
          },
        ],
        payment_number: defaultValue?.data.payment_number,
      });
    }
  }, [defaultValue, reset, searchParams, setValue]);

  const handleMutate = (data: any) => {
    // console.log("THIS IS THE DATA INSIDE THE HANDLEMUTATE", data);
    const extractNumber = parseInt(
      data?.total_paid_amount.replace(/\D/g, ""),
      10,
    );

    data = {
      ...data,
      student: data["student"][0]["value"],
      course_batch: data["course_batch"][0]["value"],
      payment_mode: data["payment_mode"][0]["value"],
      invoice: data["invoice"][0]["value"],
      total_paid_amount: extractNumber,
    };

    if (data?.discount_type?.length > 0) {
      data.discount_type = data["discount_type"][0]["value"];
    } else {
      data.discount_type = null;
    }
    if (data?.discount?.length > 0) {
      data.discount = data["discount"][0]["value"];
    } else {
      data.discount = null;
    }
    if (data.discount_code) {
      data.discount_code = data.discount_code;
    } else {
      data.discount_code = "";
    }

    if (
      discountData &&
      (watch("discount")?.length != 0 || watch("discount_code"))
    ) {
      // console.log("I was here again!!!!!!");
      // console.log("I was here again!!!!!!", watch("discount"));
      // console.log("I was here again!!!!!!", watch("discount_code"));
      data.discount_value = discountData?.data?.discount_amount;
      data.due_amount_inv = discountData?.data?.final_amount_after_discount;
      // } else if (watch("custom_discount")) {
      //   data.discount_value = Number(watch("custom_discount"));
      //   data.due_amount_inv =
      //     Number(invoiceData?.data.results[0].due_amount) -
      //     Number(watch("custom_discount") || 0);
    } else {
      data.discount_value = 0;
      data.due_amount_inv = Number(invoiceData?.data.results[0].due_amount);
    }

    // console.log("THis is the exact amount", extractNumber);
    // console.log("This is discount value", data.discount_value);
    // console.log("this is the due amount inv", data.due_amount_inv);

    if (pathname == "masterclass-payment") data.is_masterclass = true;
    if (isJrp) data.is_jrp = true;
    if (modalItem) {
      data = {
        ...data,
        id: modalItem,
      };
    }
    return {
      data,
      endpoint: endpoint["payment"],
      method: modalItem ? METHODS.PUT : METHODS.POST,
    };
  };

  const calculateTotalDiscount = (hasDiscount: any) => {
    let totalDiscount = 0;

    // Add API discount if exists
    if (hasDiscount && discountData?.data?.discount_amount != null) {
      totalDiscount += Number(discountData.data.discount_amount);
    }

    // Add custom discount if exists
    if (watch("custom_discount") && watch("custom_discount") !== "") {
      totalDiscount += Number(watch("custom_discount"));
    }

    return totalDiscount;
  };

  const calculateFinalAmount = (item: any, hasDiscount: any) => {
    const dueAmount = Number(item?.due_amount);
    const totalDiscount = calculateTotalDiscount(hasDiscount);
    return dueAmount - totalDiscount;
  };

  const renderDiscountAmount = (hasDiscount: any) => {
    const hasApiDiscount = hasDiscount && discountData?.data?.discount_amount;
    const hasCustomDiscount =
      watch("custom_discount") && watch("custom_discount") !== "";

    if (!hasApiDiscount && !hasCustomDiscount) {
      return "N/A";
    }

    return (
      <>
        {hasApiDiscount && (
          <div>NRs. {Number(discountData.data.discount_amount)}</div>
        )}
        {hasCustomDiscount && (
          <div>NRs. {Number(watch("custom_discount"))}</div>
        )}
      </>
    );
  };

  const handleSuccess = async ({
    status,
  }: {
    data: PaymentData;
    status: number;
  }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      showToast({
        description: `Payment ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "modal-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const { setIsFormPending } = useSelectionStore();
  const handleError = async (data: any) => {
    setIsFormPending(false);

    throwErrorAlert(data);
  };

  const { mutate, isPending } = usePostData();

  const onSubmit = (data: PayementFormData) => {
    if (watch("custom_discount") == "") {
      data.custom_discount = "0";
    }
    console.log("This is the payment mutate", handleMutate(data));
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  useEffect(() => {
    if (!modalItem) {
      // Only affect in add mode, not edit mode
      const discountType = watch("discount_type");

      if (!discountType || discountType.length === 0) {
        setValue("discount", []);
        setValue("discount_code", "");
      }
    } else {
      // In edit mode, only clear when discount_type actually changes from a value to empty
      const discountType = watch("discount_type");

      if (
        (!discountType || discountType.length === 0) &&
        dirtyFields.discount_type
      ) {
        setValue("discount", []);
        setValue("discount_code", "");
      }
    }
  }, [watch("discount_type"), modalItem, setValue, dirtyFields.discount_type]);

  const formSchema: FormField[] = [
    {
      type: "component",
      colSpan: 11,
      component: (
        <div className="!w-full pt-[20px]">
          <div className="text-[12px] mb-[16px] w-full flex justify-between items-center">
            <div className="space-x-2">
              <span className="font-[600]">Invoice Date</span>
              <span>
                {" "}
                <input
                  type="date"
                  className="outline-0"
                  {...register("date")}
                />{" "}
                {errors?.date && (
                  <span className="text-red-600 block">
                    {errors?.date?.message}
                  </span>
                )}
              </span>
            </div>
            <div className="space-x-2 flex items-center">
              <span className="font-[600]">Payment No.:</span>
              <span className="flex">
                {isFetching ? (
                  <Spinner className="h-[10px] w-[10px]" />
                ) : (
                  watch("payment_number")
                )}
              </span>
            </div>
          </div>

          <ul className="list-none text-[12px]">
            <li className="font-[600]">Student Name</li>
            <li>{studentData?.data?.email || "Your Email"} </li>
            <li>{studentData?.data?.user?.phone || "Your Phone"}</li>
          </ul>

          {invoiceDataPending ? (
            <div className="h-full flex items-center justify-center ">
              Finding Invoice <Spinner />
            </div>
          ) : (
            invoiceData?.data?.results &&
            invoiceData?.data?.results.map((item: any) => {
              // right above the table render
              const hasDiscount =
                (Array.isArray(watch("discount")) &&
                  (watch("discount")?.length ?? 0) > 0) ||
                Boolean(debouncedValue);

              return (
                <>
                  {" "}
                  <table className="text-[11px] mt-[32px]">
                    <thead>
                      <tr>
                        <th className="text-start lg:w-[200px] w-[100px]">
                          Course
                        </th>
                        <th className="text-end w-[100px]">Total Amount</th>
                        <th className="text-end w-[100px]">Due Amount</th>
                        <th className="text-end text-nowrap w-[100px]">
                          Discount Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <div className="text-[12px]">
                            <div className="font-[600] max-w-[150px] w-fit truncate">
                              {item?.course_batch?.course?.title}
                            </div>
                            <div className="text-[10px]  max-w-[150px] w-fit truncate font-[400]">
                              {item?.course_batch?.name}
                            </div>
                          </div>
                        </td>
                        <td className="text-end">NRs. {item?.total_amount}</td>
                        <td className="text-end">{item?.due_amount}</td>
                        <td className="text-end">
                          {renderDiscountAmount(hasDiscount)}
                        </td>
                      </tr>
                      <tr>
                        <td></td>
                        <td className="font-[700] text-end">
                          Due Amount Total
                        </td>
                        <td className="font-[700] text-end">
                          NRs.
                          {calculateFinalAmount(item, hasDiscount)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </>
              );
            })
          )}
        </div>
      ),
    },
    {
      name: "student",
      label: "Select Student",

      endpoint: endpoint["studentselect"],
      format: { key: "full_name", value: "id", secondary_key: "email" },
      colSpan: 12,
      type: "async",
      onChange: () => {
        console.log("ss", watch("student"));
        setValue("course_batch", []);
        setValue("invoice", []);
      },
      placeholder: "Select Student",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
      disabled: modalItem ? true : false,
    },
    {
      name: "course_batch",
      label: "Select Course & Batch",
      colSpan: 12,
      type: "async",
      disabled: watch("student")?.length > 0 && !modalItem ? false : true,
      endpoint:
        watch("student")?.length > 0
          ? endpoint["course-batch"] +
            "student/" +
            watch("student")?.[0]?.["value"] +
            "/"
          : endpoint["course-batch"],
      filter:
        pathname == "masterclass-payment"
          ? { is_masterclass: true }
          : isJrp
            ? { is_jrp: true }
            : { is_masterclass: false },
      format: { key: "name", value: "id" },
      placeholder: "Select Course & Batch",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "invoice",
      label: "Select Invoice",
      disabled: !(
        watch("student")?.length > 0 && watch("course_batch")?.length > 0
      ),
      // && (!modalItem ? false : true)
      colSpan: 12,
      type: "async",
      endpoint:
        watch("student") && watch("course_batch")
          ? endpoint["invoice-by-student"]
          : endpoint["invoice"],
      filter:
        watch("student") && watch("course_batch")
          ? {
              course_batch__name: watch("course_batch")[0]?.["label"],
              student_id: watch("student")[0]?.["value"],
            }
          : {},
      format: { key: "invoice_no", value: "id" },
      placeholder: "Select The Invoice Number",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "payment_mode",
      label: "Select Payment Mode",
      colSpan: 6,
      type: "async",
      endpoint: endpoint["paymentmode"],
      format: { key: "name", value: "id" },
      placeholder: "Select Payment Mode",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "discount_type",
      label: (
        <>
          Discount Apply{" "}
          <span className="text-slate-300 font-[400] text-[12px] select-none">
            (optional)
          </span>{" "}
        </>
      ),
      colSpan: 6,
      type: "async",
      format: { key: "name", value: "id" },
      option: [
        { label: "Discount type", value: "discount_type" },
        { label: "Discount code", value: "discount_code" },
      ],
      placeholder: "Select Discount",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "discount_code",
      label: "Discount Code",
      colSpan: 6,
      type: "text",
      placeholder: "Enter Discount Code",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable:
        Array.isArray(watch("discount_type")) &&
        watch("discount_type")?.[0]?.["value"] === "discount_code"
          ? true
          : false,
    },
    {
      name: "discount",
      label: <>Discount </>,
      colSpan: 6,
      type: "async",
      isMulti: false,
      endpoint: endpoint["discount"],
      format: { key: "name", value: "id" },
      placeholder: "Select Discount",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable:
        Array.isArray(watch("discount_type")) &&
        watch("discount_type")?.[0]?.["value"] === "discount_type"
          ? true
          : false,
    },
    {
      name: "custom_discount",
      label: <>Custom Discount</>,
      colSpan: 6,
      type: "text",
      isMulti: false,
      placeholder: "Add discount",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "total_paid_amount",
      label: "Payment Amount",
      colSpan: 12,
      type: "prefixinput",
      prefixType: "number",
      prefix:
        invoiceData?.data?.results[0]?.course_batch?.course?.currency || "NPR",
      placeholder: "Enter Amount",
      control: control,
      setValue: setValue,
      errors,
      min: 0,
      max: Array.isArray(invoiceData?.data)
        ? invoiceData?.data[0]?.due_amount
        : undefined,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "component",
      component: (
        <div className="flex gap-2 items-center">
          <label
            htmlFor={isJrp ? "is_jrp" : "is_masterclass"}
            className="cursor-pointer"
          >
            {isJrp ? "Is JRP" : "Is Masterclass"}
          </label>
          <Controller
            control={control}
            name={isJrp ? "is_jrp" : "is_masterclass"}
            render={({ field: { onChange, value } }) => (
              <Switch
                id={isJrp ? "is_jrp" : "is_masterclass"}
                checked={true}
                onCheckedChange={() => {}}
                disabled
              />
            )}
          />
        </div>
      ),
      colSpan: 12,
      visable: ["masterclass-payment", "jrp-payment"].includes(pathname),
    },
    {
      type: "submit",
      value: `${searchParams.get("modal") || ""} ${(type || pathname)?.replace(
        "-",
        " ",
      )}`,
      className: "w-full h-[47px] mt-[32px] capitalize",
      colSpan: 12,
      variant: "default",
      endIcon: isPending ? <Spinner /> : null,
      disabled: isPending ? true : false,
      visable: true,
    },
  ];

  if (fetchPending && modalItem)
    return (
      <>
        <FormSkeleton />
      </>
    );

  return (
    <>
      <form
        key={JSON.stringify(getValues("student"))}
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full"
      >
        <div className="col-md-6">
          <div className="row">
            {formSchema.slice(0, 1).map((field, index) => {
              console.log("this is field data", field);
              return (
                (field.visable || field.visable == undefined) && (
                  <div
                    key={index}
                    className={`col-lg-${field.colSpan} col-6 lg:px-[6px] px-[8px] overflow-auto`}
                  >
                    <div className="h-180">{getInputField({ ...field })}</div>
                  </div>
                )
              );
            })}
          </div>
        </div>
        <div className="col-md-6 overflow-auto">
          <div className="row ">
            {formSchema.slice(1, 11).map((field, index) => {
              console.log("this is field", field);
              return (
                (field.visable || field.visable == undefined) && (
                  <div
                    key={index}
                    className={`col-lg-${field.colSpan} col-12 lg:px-[6px] px-[8px]`}
                  >
                    {getInputField({ ...field })}
                  </div>
                )
              );
            })}
            <div className="mt-2">
              <Button
                type="submit"
                className="btn btn-primary w-full"
                disabled={isPending}
              >
                Add Payment {isPending && <Spinner />}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default PaymentForm;
